import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Switch, Chip, Pagination, Tooltip
} from "@mui/material";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import EventIcon from "@mui/icons-material/Event";
import ViewCarouselIcon from "@mui/icons-material/ViewCarousel";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { fetchData, postData, putData, deleteData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";

const PAGE_SIZE = 10;

const FALLBACK_EVENTS = [
  { _id: "1", title: "Annual Day 2025", content: "Celebrate our students' achievements at the Annual Day event.", eventDate: "2025-11-15", imageUrl: "", isPublished: true, isBanner: true },
  { _id: "2", title: "Tamil New Year Celebration", content: "Join us for a vibrant Tamil New Year celebration with traditional performances.", eventDate: "2026-04-14", imageUrl: "", isPublished: true, isBanner: false },
  { _id: "3", title: "Parent-Teacher Meeting", content: "Meet teachers and discuss your child's progress.", eventDate: "2026-02-20", imageUrl: "", isPublished: false, isBanner: false },
];

const emptyForm = { title: "", content: "", eventDate: "", imageFile: null, imagePreview: "", isPublished: true, isBanner: false };

function ConfirmDialog({ open, onClose, onConfirm }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, fontSize: "16px" }}>Confirm Delete</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: "14px", color: "#374151" }}>Are you sure you want to delete this event?</Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: "none", color: "#6B7280" }}>Cancel</Button>
        <Button onClick={onConfirm} variant="contained" color="error" sx={{ textTransform: "none", borderRadius: "8px" }}>Delete</Button>
      </DialogActions>
    </Dialog>
  );
}

const fmtDate = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }); }
  catch { return d; }
};

const AdminEventConfig = () => {
  const [events, setEvents] = useState([]);
  const [bannerPage, setBannerPage] = useState(1);
  const [eventsPage, setEventsPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const load = useCallback(async () => {
    try {
      const res = await fetchData("admin/program-events/list");
      setEvents(Array.isArray(res?.data) ? res.data : FALLBACK_EVENTS);
    } catch { setEvents(FALLBACK_EVENTS); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const showToast = (severity, message) => setToast({ open: true, severity, message });

  const openAdd = () => { setEditId(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (row) => {
    setEditId(row._id || row.id);
    setForm({
      title: row.title || "", content: row.content || "",
      eventDate: row.eventDate || "", imageFile: null,
      imagePreview: row.imageUrl || "",
      isPublished: row.isPublished !== false,
      isBanner: row.isBanner === true,
    });
    setDialogOpen(true);
  };
  const closeDialog = () => { setDialogOpen(false); setForm(emptyForm); setEditId(null); };

  const handleSave = async () => {
    if (!form.title.trim()) { showToast("error", "Title is required."); return; }
    const fd = new FormData();
    fd.append("title", form.title);
    fd.append("content", form.content);
    fd.append("eventDate", form.eventDate);
    fd.append("isPublished", form.isPublished);
    fd.append("isBanner", form.isBanner);
    if (form.imageFile) fd.append("image", form.imageFile);
    try {
      if (editId) {
        await putData(`admin/program-events/update/${editId}`, fd);
        showToast("success", "Event updated.");
      } else {
        await postData("admin/program-events/create", fd);
        showToast("success", "Event added.");
      }
      closeDialog(); load();
    } catch { showToast("error", "Failed to save."); }
  };

  const handleDelete = async () => {
    const id = deleteTarget?._id || deleteTarget?.id;
    setConfirmOpen(false);
    try {
      await deleteData(`admin/program-events/${id}`);
      showToast("success", "Event deleted.");
      load();
    } catch { showToast("error", "Failed to delete."); }
  };

  const handleUpdate = () => showToast("success", "Events configuration saved.");

  const bannerRows = events.filter(e => e.isBanner);
  const eventRows = events.filter(e => !e.isBanner);

  const bannerPageData = bannerRows.slice((bannerPage - 1) * PAGE_SIZE, bannerPage * PAGE_SIZE);
  const eventsPageData = eventRows.slice((eventsPage - 1) * PAGE_SIZE, eventsPage * PAGE_SIZE);

  const btnStyle = {
    background: "linear-gradient(90deg, #4A7BA8, #244C77)",
    color: "#fff", fontSize: "13px", textTransform: "none",
    borderRadius: "10px", px: 2.5, py: 1,
    "&:hover": { background: "linear-gradient(90deg, #4A7BA8, #244C77)" },
  };

  const renderImageCell = (url) => url ? (
    <img src={url} alt="event" style={{ width: 60, height: 60, objectFit: "cover", borderRadius: "8px", border: "1px solid #E5E7EB" }} />
  ) : (
    <Box sx={{ width: 60, height: 60, borderRadius: "8px", backgroundColor: "#F3F4F6", display: "flex", alignItems: "center", justifyContent: "center", border: "1px dashed #D1D5DB" }}>
      <AddPhotoAlternateIcon sx={{ color: "#9CA3AF", fontSize: "20px" }} />
    </Box>
  );

  const actionCells = (row) => (
    <Box sx={{ display: "flex", gap: 0.5 }}>
      <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(row)} sx={{ color: "#244C77", "&:hover": { backgroundColor: "#EEF2FF" } }}><EditRoundedIcon fontSize="small" /></IconButton></Tooltip>
      <Tooltip title="Delete"><IconButton size="small" onClick={() => { setDeleteTarget(row); setConfirmOpen(true); }} sx={{ color: "#EF4444", "&:hover": { backgroundColor: "#FEF2F2" } }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
    </Box>
  );

  return (
    <div style={{ textAlign: "left", padding: "1rem" }}>
      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })} severity={toast.severity} message={toast.message} />
      <ConfirmDialog open={confirmOpen} onClose={() => setConfirmOpen(false)} onConfirm={handleDelete} />

      <p style={{ color: "rgba(0,0,0,0.45)", fontWeight: "bold", fontSize: "14px", margin: "0 0 12px" }}>
        Events / Program Events displayed in the website
      </p>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <EventIcon sx={{ color: "#244C77", fontSize: "28px" }} />
          <Box>
            <Typography sx={{ fontWeight: "bold", fontSize: "22px", color: "#111827" }}>Program Events</Typography>
            <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>Manage event banners and listings displayed on the website</Typography>
          </Box>
        </Box>
        <Button startIcon={<AddIcon />} onClick={openAdd} sx={btnStyle}>+ Add Event</Button>
      </Box>

      {/* Table 1: Banner */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
          <ViewCarouselIcon sx={{ color: "#244C77", fontSize: "20px" }} />
          <Typography sx={{ fontWeight: 700, fontSize: "16px", color: "#111827" }}>Banner</Typography>
        </Box>
        <Paper elevation={0} sx={{ borderRadius: "16px", boxShadow: "0 2px 12px rgba(0,0,0,0.07)", overflow: "hidden" }}>
          <TableContainer>
            <Table>
              <TableHead sx={{ backgroundColor: "#1E293B" }}>
                <TableRow>
                  {["S.No", "Image", "Title", "Actions"].map(h => (
                    <TableCell key={h} sx={{ color: "#fff", fontWeight: 600, fontSize: "13px" }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {bannerPageData.length === 0 ? (
                  <TableRow><TableCell colSpan={4} align="center" sx={{ py: 3, color: "#9CA3AF" }}>No banner events. Mark events as banner when adding.</TableCell></TableRow>
                ) : bannerPageData.map((row, idx) => (
                  <TableRow key={row._id || idx} sx={{ "&:hover": { backgroundColor: "#f9fafb" }, backgroundColor: idx % 2 === 0 ? "#fff" : "#FAFAFA" }}>
                    <TableCell sx={{ fontSize: "13px", width: 60 }}>{(bannerPage - 1) * PAGE_SIZE + idx + 1}</TableCell>
                    <TableCell>{renderImageCell(row.imageUrl)}</TableCell>
                    <TableCell sx={{ fontSize: "13px", fontWeight: 600, color: "#111827" }}>{row.title}</TableCell>
                    <TableCell>{actionCells(row)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {bannerRows.length > PAGE_SIZE && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <Pagination count={Math.ceil(bannerRows.length / PAGE_SIZE)} page={bannerPage} onChange={(_, v) => setBannerPage(v)} color="primary" size="small" />
            </Box>
          )}
        </Paper>
      </Box>

      {/* Table 2: Events List */}
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
          <EventIcon sx={{ color: "#244C77", fontSize: "20px" }} />
          <Typography sx={{ fontWeight: 700, fontSize: "16px", color: "#111827" }}>Events List</Typography>
        </Box>
        <Paper elevation={0} sx={{ borderRadius: "16px", boxShadow: "0 2px 12px rgba(0,0,0,0.07)", overflow: "hidden" }}>
          <TableContainer>
            <Table>
              <TableHead sx={{ backgroundColor: "#1E293B" }}>
                <TableRow>
                  {["S.No", "Image", "Title", "Content", "Date", "Published", "Actions"].map(h => (
                    <TableCell key={h} sx={{ color: "#fff", fontWeight: 600, fontSize: "13px" }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {eventsPageData.length === 0 ? (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: "#9CA3AF" }}>No events yet. Click "+ Add Event" to add one.</TableCell></TableRow>
                ) : eventsPageData.map((row, idx) => (
                  <TableRow key={row._id || idx} sx={{ "&:hover": { backgroundColor: "#f9fafb" }, backgroundColor: idx % 2 === 0 ? "#fff" : "#FAFAFA" }}>
                    <TableCell sx={{ fontSize: "13px", width: 60 }}>{(eventsPage - 1) * PAGE_SIZE + idx + 1}</TableCell>
                    <TableCell>{renderImageCell(row.imageUrl)}</TableCell>
                    <TableCell sx={{ fontSize: "13px", fontWeight: 600, color: "#111827", maxWidth: 160 }}>
                      <Typography noWrap sx={{ fontSize: "13px", maxWidth: 150 }}>{row.title}</Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200 }}>
                      <Typography noWrap sx={{ fontSize: "13px", color: "#6B7280", maxWidth: 190 }}>{row.content}</Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: "13px", color: "#374151", whiteSpace: "nowrap" }}>{fmtDate(row.eventDate)}</TableCell>
                    <TableCell>
                      <Chip label={row.isPublished ? "Published" : "Draft"} size="small"
                        sx={{
                          backgroundColor: row.isPublished ? "#F0FDF4" : "#F3F4F6",
                          color: row.isPublished ? "#16A34A" : "#6B7280",
                          fontWeight: 600, fontSize: "11px",
                        }} />
                    </TableCell>
                    <TableCell>{actionCells(row)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {eventRows.length > PAGE_SIZE && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <Pagination count={Math.ceil(eventRows.length / PAGE_SIZE)} page={eventsPage} onChange={(_, v) => setEventsPage(v)} color="primary" size="small" />
            </Box>
          )}
        </Paper>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
        <Button startIcon={<SaveOutlinedIcon />} onClick={handleUpdate} sx={btnStyle}>Update</Button>
      </Box>

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: "17px", color: "#111827" }}>
          {editId ? "Edit Event" : "Add Event"}
        </DialogTitle>
        <DialogContent sx={{ pt: "12px !important" }}>
          <TextField label="Title" fullWidth size="small" value={form.title}
            onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
            sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
          <TextField label="Content" fullWidth size="small" multiline rows={3} value={form.content}
            onChange={e => setForm(p => ({ ...p, content: e.target.value }))}
            sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
          <TextField label="Event Date" fullWidth size="small" type="date" value={form.eventDate}
            onChange={e => setForm(p => ({ ...p, eventDate: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />

          {/* Image Upload */}
          <Box component="label" sx={{
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            border: "2px dashed #D1D5DB", borderRadius: "10px", height: 140, cursor: "pointer",
            backgroundColor: "#F9FAFB", "&:hover": { borderColor: "#4A7BA8" }, overflow: "hidden", mb: 2,
          }}>
            {form.imagePreview ? (
              <img src={form.imagePreview} alt="preview" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            ) : (
              <>
                <AddPhotoAlternateIcon sx={{ fontSize: "36px", color: "#9CA3AF", mb: 0.5 }} />
                <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>Click to upload image</Typography>
              </>
            )}
            <input type="file" hidden accept=".jpg,.jpeg,.png,.webp" onChange={e => {
              const f = e.target.files[0];
              if (f) setForm(p => ({ ...p, imageFile: f, imagePreview: URL.createObjectURL(f) }));
            }} />
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography sx={{ fontSize: "13px", color: "#374151" }}>Is Published</Typography>
              <Switch checked={form.isPublished} onChange={e => setForm(p => ({ ...p, isPublished: e.target.checked }))} size="small"
                sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#4caf50" }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#4caf50" } }} />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography sx={{ fontSize: "13px", color: "#374151" }}>Is Banner</Typography>
              <Switch checked={form.isBanner} onChange={e => setForm(p => ({ ...p, isBanner: e.target.checked }))} size="small"
                sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#244C77" }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#244C77" } }} />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDialog} sx={{ textTransform: "none", color: "#6B7280" }}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" sx={{ ...btnStyle, px: 3, py: 0.8 }}>
            {editId ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AdminEventConfig;
